import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
}

interface Question {
  id: string;
  question: string;
  options: string[];
  difficulty: string;
}

interface QuizResult {
  score: number;
  totalQuestions: number;
  percentage: number;
  passed: boolean;
  answers: Array<{
    questionId: string;
    correct: boolean;
    correctAnswer: number;
    explanation: string;
  }>;
}

interface QuizSession {
  id: string;
  categoryId: string;
  categoryName?: string;
  questions: Question[];
  answers: Record<string, number>;
  flaggedQuestions: string[];
  currentQuestionIndex: number;
  createdAt: string;
  updatedAt: string;
}

export default function QuizPage() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<string>>(new Set());
  const [showReview, setShowReview] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<QuizResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('QuizPage mounted, loading categories...');
    loadCategories();
    
    // Check if resuming a session from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const resumeSessionId = urlParams.get('resume');
    if (resumeSessionId) {
      loadAndResumeSession(resumeSessionId);
    }
  }, []);

  // Auto-save when answers or flags change
  useEffect(() => {
    if (sessionId && questions.length > 0) {
      const timeoutId = setTimeout(() => {
        autoSaveSession();
      }, 1000); // Debounce 1 second
      return () => clearTimeout(timeoutId);
    }
  }, [answers, flaggedQuestions, currentQuestionIndex, sessionId]);

  const loadCategories = async (): Promise<Category[]> => {
    try {
      console.log('Loading categories from API...');
      const response = await api.get('/quizzes/categories');
      console.log('Categories loaded:', response.data);
      setCategories(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load categories:', error);
      return [];
    }
  };

  const checkExistingSession = async (categoryId: string): Promise<QuizSession | null> => {
    try {
      const response = await api.get('/quizzes/sessions');
      const sessions: QuizSession[] = response.data;
      const existingSession = sessions.find(s => s.categoryId === categoryId);
      return existingSession || null;
    } catch (error) {
      console.error('Failed to check existing session:', error);
      return null;
    }
  };

  const resumeSession = async (session: QuizSession) => {
    console.log('Resuming session:', session);
    setQuestions(session.questions);
    setSelectedCategory(categories.find(c => c.id === session.categoryId) || null);
    setCurrentQuestionIndex(session.currentQuestionIndex);
    setAnswers(session.answers);
    setFlaggedQuestions(new Set(session.flaggedQuestions));
    setSessionId(session.id);
    setShowReview(false);
    setShowResults(false);
  };

  const loadAndResumeSession = async (sessionId: string) => {
    try {
      setLoading(true);
      console.log('Loading session from URL:', sessionId);
      
      // Load categories first and get the array
      let loadedCategories = categories;
      if (categories.length === 0) {
        loadedCategories = await loadCategories();
      }
      
      // Fetch the specific session
      const response = await api.get(`/quizzes/sessions/${sessionId}`);
      const session = response.data;
      
      if (session) {
        // Find the category from the loaded categories
        const category = loadedCategories.find(c => c.id === session.categoryId);
        
        if (category) {
          // Set all the quiz state
          setQuestions(session.questions);
          setSelectedCategory(category);
          setCurrentQuestionIndex(session.currentQuestionIndex);
          setAnswers(session.answers);
          setFlaggedQuestions(new Set(session.flaggedQuestions));
          setSessionId(session.id);
          setShowReview(false);
          setShowResults(false);
          
          console.log('Session resumed successfully');
        } else {
          setError('Quiz category not found');
        }
        
        // Clear the URL parameter
        window.history.replaceState({}, '', '/quiz');
      } else {
        setError('Session not found');
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      setError('Failed to load saved quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async (category: Category) => {
    console.log('startQuiz called with category:', category);
    
    // Check for existing session
    const existingSession = await checkExistingSession(category.id);
    if (existingSession) {
      const resume = window.confirm(
        `You have an in-progress quiz for ${category.name} (${Object.keys(existingSession.answers).length}/${existingSession.questions.length} answered). Would you like to resume it?`
      );
      if (resume) {
        await resumeSession(existingSession);
        return;
      } else {
        // Delete old session and start fresh
        try {
          await api.delete(`/quizzes/sessions/${existingSession.id}`);
        } catch (error) {
          console.error('Failed to delete old session:', error);
        }
      }
    }

    setLoading(true);
    setError(null);
    try {
      console.log('Fetching questions from:', `/quizzes/categories/${category.id}/questions?limit=20`);
      const response = await api.get(`/quizzes/categories/${category.id}/questions?limit=20`);
      console.log('Questions response:', response.data);
      if (!response.data || response.data.length === 0) {
        setError('No questions available for this category');
        return;
      }
      
      const newQuestions = response.data;
      setQuestions(newQuestions);
      setSelectedCategory(category);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setFlaggedQuestions(new Set());
      setShowReview(false);
      setShowResults(false);
      setResults(null);

      // Create new session
      const sessionResponse = await api.post('/quizzes/sessions', {
        categoryId: category.id,
        questions: newQuestions,
        answers: {},
        flaggedQuestions: [],
        currentQuestionIndex: 0,
      });
      setSessionId(sessionResponse.data.sessionId);
      console.log('New session created:', sessionResponse.data.sessionId);
    } catch (error: any) {
      console.error('Failed to load questions:', error);
      setError(error.response?.data?.error || error.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const autoSaveSession = async () => {
    if (!sessionId) return;
    
    setAutoSaving(true);
    try {
      await api.put(`/quizzes/sessions/${sessionId}`, {
        answers,
        flaggedQuestions: Array.from(flaggedQuestions),
        currentQuestionIndex,
      });
      console.log('Session auto-saved');
    } catch (error) {
      console.error('Failed to auto-save session:', error);
    } finally {
      setAutoSaving(false);
    }
  };

  const saveAndExit = async () => {
    if (!sessionId) {
      resetQuiz();
      return;
    }

    await autoSaveSession(); // Final save
    setShowSaveDialog(true);
  };

  const selectAnswer = (questionId: string, answerIndex: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const toggleFlag = (questionId: string) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const goToQuestion = (index: number) => {
    setCurrentQuestionIndex(index);
    setShowReview(false);
  };

  const submitQuiz = async () => {
    if (!selectedCategory) return;
    
    setLoading(true);
    try {
      const answersArray = questions.map(q => ({
        questionId: q.id,
        selectedAnswer: answers[q.id] ?? -1,
      }));

      const response = await api.post('/quizzes/attempts', {
        categoryId: selectedCategory.id,
        answers: answersArray,
      });

      setResults(response.data);
      setShowResults(true);
      setShowReview(false);

      // Delete session after successful submission
      if (sessionId) {
        try {
          await api.delete(`/quizzes/sessions/${sessionId}`);
          setSessionId(null);
        } catch (error) {
          console.error('Failed to delete session:', error);
        }
      }
    } catch (error) {
      console.error('Failed to submit quiz:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetQuiz = () => {
    setSelectedCategory(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setFlaggedQuestions(new Set());
    setShowReview(false);
    setShowResults(false);
    setResults(null);
    setSessionId(null);
  };

  // Calculate stats
  const answeredCount = Object.keys(answers).length;
  const flaggedCount = flaggedQuestions.size;
  const allAnswered = questions.every(q => answers[q.id] !== undefined);

  // Render main content based on current view
  const renderContent = () => {
    // Results view
    if (showResults && results) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="card">
            <div className="text-center mb-8">
              <div className={`text-6xl mb-4 ${results.passed ? 'text-green-400' : 'text-yellow-400'}`}>
                {results.passed ? '✓' : '⚠'}
              </div>
              <h2 className="text-3xl font-bold mb-2">
                {results.passed ? 'Congratulations!' : 'Keep Practicing'}
              </h2>
              <p className="text-slate-400">
                You scored {results.score} out of {results.totalQuestions} ({results.percentage}%)
              </p>
              {results.passed ? (
                <p className="text-green-400 mt-2">You passed! Great job!</p>
              ) : (
                <p className="text-yellow-400 mt-2">You need 80% to pass. Review the explanations below and try again.</p>
              )}
            </div>

            <div className="space-y-4 mb-8">
              {results.answers.map((answer, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    answer.correct
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`text-2xl ${answer.correct ? 'text-green-400' : 'text-red-400'}`}>
                      {answer.correct ? '✓' : '✗'}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium mb-2">Question {index + 1}</p>
                      {!answer.correct && (
                        <p className="text-sm text-slate-400 mb-2">
                          Correct answer: Option {answer.correctAnswer + 1}
                        </p>
                      )}
                      <p className="text-sm text-slate-300">{answer.explanation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button onClick={resetQuiz} className="btn btn-secondary flex-1">
                Back to Categories
              </button>
              <button
                onClick={() => startQuiz(selectedCategory!)}
                className="btn btn-primary flex-1"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Review mode view
  if (showReview && selectedCategory && questions.length > 0) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-6xl mx-auto">
          <div className="card">
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2">Review Your Answers</h2>
              <p className="text-slate-400">
                Review all questions before submitting. Click any question to edit your answer.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-800/50 rounded-lg">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">{answeredCount}</p>
                <p className="text-xs text-slate-400">Answered</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-400">{questions.length - answeredCount}</p>
                <p className="text-xs text-slate-400">Unanswered</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-400">{flaggedCount}</p>
                <p className="text-xs text-slate-400">Flagged</p>
              </div>
            </div>

            {/* Answered Questions Section */}
            {answeredCount > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-green-400">
                  <span>✓</span>
                  Answered Questions ({answeredCount})
                </h3>
                <div className="space-y-4">
                  {questions.map((q, index) => {
                    const isAnswered = answers[q.id] !== undefined;
                    if (!isAnswered) return null;
                    
                    const selectedOptionText = q.options[answers[q.id]];
                    
                    return (
                      <div key={q.id} className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg hover:bg-green-500/20 transition-colors">
                        <div className="flex items-start gap-3">
                          <span className="text-green-400 font-bold text-sm mt-1">Q{index + 1}</span>
                          <div className="flex-1">
                            <p className="text-slate-200 mb-2">{q.question}</p>
                            <p className="text-sm text-green-400 flex items-center gap-2">
                              <span>Your answer:</span>
                              <span className="font-semibold">{selectedOptionText}</span>
                            </p>
                          </div>
                          <button
                            onClick={() => goToQuestion(index)}
                            className="btn btn-sm btn-secondary"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Flagged Questions Section */}
            {flaggedCount > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-purple-400">
                  <span>🚩</span>
                  Flagged for Review ({flaggedCount})
                </h3>
                <div className="space-y-4">
                  {questions.map((q, index) => {
                    const isFlagged = flaggedQuestions.has(q.id);
                    if (!isFlagged) return null;
                    
                    const isAnswered = answers[q.id] !== undefined;
                    const selectedOptionText = q.options[answers[q.id]];
                    
                    return (
                      <div key={q.id} className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg hover:bg-purple-500/20 transition-colors">
                        <div className="flex items-start gap-3">
                          <span className="text-purple-400 font-bold text-sm mt-1">Q{index + 1}</span>
                          <div className="flex-1">
                            <p className="text-slate-200 mb-2">{q.question}</p>
                            {isAnswered ? (
                              <p className="text-sm text-purple-400 flex items-center gap-2">
                                <span>Your answer:</span>
                                <span className="font-semibold">{selectedOptionText}</span>
                              </p>
                            ) : (
                              <p className="text-sm text-yellow-400">Not answered yet</p>
                            )}
                          </div>
                          <button
                            onClick={() => goToQuestion(index)}
                            className="btn btn-sm btn-secondary"
                          >
                            {isAnswered ? 'Edit' : 'Answer'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Unanswered Questions Section */}
            {questions.length - answeredCount > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-yellow-400">
                  <span>⚠</span>
                  Unanswered Questions ({questions.length - answeredCount})
                </h3>
                <div className="space-y-4">
                  {questions.map((q, index) => {
                    const isAnswered = answers[q.id] !== undefined;
                    if (isAnswered) return null;
                    
                    return (
                      <div key={q.id} className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg hover:bg-yellow-500/20 transition-colors">
                        <div className="flex items-start gap-3">
                          <span className="text-yellow-400 font-bold text-sm mt-1">Q{index + 1}</span>
                          <div className="flex-1">
                            <p className="text-slate-200">{q.question}</p>
                          </div>
                          <button
                            onClick={() => goToQuestion(index)}
                            className="btn btn-sm btn-primary"
                          >
                            Answer
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => setShowReview(false)}
                className="btn btn-secondary"
              >
                Back to Quiz
              </button>
              <button
                onClick={saveAndExit}
                className="btn btn-secondary"
              >
                Save & Exit
              </button>
              <button
                onClick={submitQuiz}
                disabled={!allAnswered || loading}
                className="btn btn-primary flex-1"
              >
                {loading ? 'Submitting...' : 'Submit Quiz'}
              </button>
            </div>
            {!allAnswered && (
              <p className="text-sm text-yellow-400 mt-3 text-center">
                ⚠️ Please answer all questions before submitting
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quiz taking view
  if (selectedCategory && questions.length > 0) {
    const currentQuestion = questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
    const isFlagged = flaggedQuestions.has(currentQuestion.id);

    return (
      <div className="min-h-screen p-8">
        <div className="max-w-4xl mx-auto">
          <div className="card">
            {/* Header with stats */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">{selectedCategory.name}</h2>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-slate-400">
                    Questions answered: <span className="text-primary-400 font-bold">{answeredCount}/{questions.length}</span>
                  </span>
                  {flaggedCount > 0 && (
                    <span className="text-sm text-purple-400">
                      🚩 {flaggedCount} flagged
                    </span>
                  )}
                  {autoSaving && (
                    <span className="text-xs text-green-400">💾 Saving...</span>
                  )}
                </div>
              </div>
              
              {/* Progress bar */}
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full bg-gradient-to-r from-primary-600 to-purple-600 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              
              {/* Question dots indicator */}
              <div className="flex gap-1 flex-wrap">
                {questions.map((q, index) => (
                  <button
                    key={q.id}
                    onClick={() => setCurrentQuestionIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentQuestionIndex
                        ? 'bg-primary-500 w-6'
                        : answers[q.id] !== undefined
                        ? 'bg-green-500'
                        : flaggedQuestions.has(q.id)
                        ? 'bg-purple-500'
                        : 'bg-slate-600'
                    }`}
                    title={`Question ${index + 1}${answers[q.id] !== undefined ? ' (answered)' : ''}${flaggedQuestions.has(q.id) ? ' (flagged)' : ''}`}
                  />
                ))}
              </div>
            </div>

            {/* Question */}
            <div className="mb-8">
              <div className="flex justify-between items-start mb-4">
                <p className="text-lg flex-1">{currentQuestion.question}</p>
                <button
                  onClick={() => toggleFlag(currentQuestion.id)}
                  className={`ml-4 text-2xl transition-all ${
                    isFlagged ? 'scale-125' : 'opacity-40 hover:opacity-100'
                  }`}
                  title={isFlagged ? 'Remove flag' : 'Flag for review'}
                >
                  🚩
                </button>
              </div>
              
              <div className="space-y-3">
                {currentQuestion.options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => selectAnswer(currentQuestion.id, index)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      answers[currentQuestion.id] === index
                        ? 'border-primary-500 bg-primary-500/10'
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <span className="font-medium mr-3">{String.fromCharCode(65 + index)}.</span>
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-4">
              <button
                onClick={previousQuestion}
                disabled={currentQuestionIndex === 0}
                className="btn btn-secondary"
              >
                Previous
              </button>
              
              <button
                onClick={() => setShowReview(true)}
                className="btn btn-secondary"
              >
                Review ({answeredCount}/{questions.length})
              </button>

              <button
                onClick={saveAndExit}
                className="btn btn-secondary"
              >
                Save & Exit
              </button>
              
              {currentQuestionIndex < questions.length - 1 ? (
                <button
                  onClick={nextQuestion}
                  className="btn btn-primary flex-1"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={() => setShowReview(true)}
                  className="btn btn-primary flex-1"
                >
                  Review & Submit
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Category selection view
  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-primary-400 hover:text-primary-300 mb-4"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold mb-2">Quiz Center</h1>
          <p className="text-slate-400">Test your knowledge and track your progress</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {categories.map(category => (
            <div key={category.id} className="card hover:border-primary-500/40 transition-colors">
              <h3 className="text-xl font-bold mb-2">{category.name}</h3>
              <p className="text-slate-400 text-sm mb-4">{category.description}</p>
              <button
                onClick={() => startQuiz(category)}
                disabled={loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Loading...' : 'Start Quiz'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
    );
  };

  // Main return with dialog overlay
  return (
    <>
      {renderContent()}
      
      {/* Save Success Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 max-w-md mx-4 border border-slate-700 shadow-xl">
            <div className="text-center">
              <div className="text-5xl mb-4">💾</div>
              <h3 className="text-xl font-bold mb-2">Progress Saved!</h3>
              <p className="text-slate-400 mb-6">
                Your progress has been saved. You can resume this quiz later from the dashboard.
              </p>
              <button
                onClick={() => {
                  setShowSaveDialog(false);
                  resetQuiz();
                }}
                className="btn btn-primary w-full"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
